import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  KeyboardAvoidingView,
  Keyboard,
  Platform,
  Alert,
  Pressable,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useHeaderHeight } from '@react-navigation/elements';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { OverflowMenu } from '../../components/OverflowMenu';
import { useAuth } from '../../contexts/AuthContext';
import { showGuestAlert } from '../../utils/guestReadOnlyAlert';
import { impactLight } from '../../utils/haptics';
import {
  subscribeMessages,
  sendMessage,
  deleteMessageForMe,
  deleteMessageForEveryone,
} from '../../services/chatService';
import type { MessageDoc } from '../../types/models';
import type { MainStackParamList } from '../../navigation/types';
import { blockUser } from '../../services/userService';

const EMPTY_CHAT_ICEBREAKERS = [
  'Ek gıda serüveni nasıl gidiyor?',
  'Uyku düzeninizi oturtabildiniz mi?',
  'Hangi parklara gidiyorsunuz?',
] as const;

type Props = NativeStackScreenProps<MainStackParamList, 'Chat'>;

export function ChatScreen({ route, navigation }: Props) {
  const insets = useSafeAreaInsets();
  const headerHeight = useHeaderHeight();
  const {
    chatId,
    otherUserId,
    initialDraftMessage,
    initialMessage,
    autoSendInitialDraft,
    hideIcebreakers,
  } = route.params;
  const resolvedInitial = initialMessage?.trim() || initialDraftMessage?.trim() || '';
  const hideIce = hideIcebreakers === true;

  const { user, logout, refreshProfile } = useAuth();
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [text, setText] = useState('');
  const listRef = useRef<FlatList<MessageDoc>>(null);
  const sentInitialRef = useRef(false);
  const appliedInitialToInputRef = useRef(false);

  useEffect(() => {
    if (!chatId || !user?.uid || user.isAnonymous) {
      setMessages([]);
      return;
    }
    const unsub = subscribeMessages(chatId, user.uid, setMessages);
    return () => unsub();
  }, [chatId, user?.uid, user?.isAnonymous]);

  useEffect(() => {
    if (user?.isAnonymous === true) {
      return;
    }
    if (messages.length > 0) {
      return;
    }
    if (!resolvedInitial || autoSendInitialDraft) {
      return;
    }
    if (appliedInitialToInputRef.current) {
      return;
    }
    setText(resolvedInitial);
    appliedInitialToInputRef.current = true;
  }, [messages.length, resolvedInitial, autoSendInitialDraft, user?.isAnonymous]);

  useEffect(() => {
    if (
      !autoSendInitialDraft ||
      !resolvedInitial ||
      !chatId ||
      !user?.uid ||
      user?.isAnonymous === true ||
      sentInitialRef.current
    ) {
      return;
    }
    const msg = resolvedInitial;
    sentInitialRef.current = true;
    void (async () => {
      try {
        await sendMessage(chatId, user.uid, msg);
      } catch (e) {
        sentInitialRef.current = false;
        Alert.alert(
          'Mesaj gönderilemedi',
          e instanceof Error ? e.message : 'Bağlantıyı kontrol edip tekrar dene.',
        );
      }
    })();
  }, [autoSendInitialDraft, resolvedInitial, chatId, user?.uid, user?.isAnonymous]);

  useLayoutEffect(() => {
    if (!user?.uid || user.isAnonymous || !otherUserId || otherUserId === user.uid) {
      navigation.setOptions({ headerRight: undefined });
      return;
    }
    navigation.setOptions({
      headerRight: () => (
        <View className="mr-1">
          <OverflowMenu
            accessibilityLabel="Sohbet seçenekleri"
            items={[
              {
                key: 'block-peer',
                label: 'Kullanıcıyı engelle',
                destructive: true,
                onPress: () => {
                  Alert.alert(
                    'Kullanıcıyı engelle',
                    'Bu kişinin içerikleri sende görünmez. Sohbetten çıkılacak.',
                    [
                      { text: 'Vazgeç', style: 'cancel' },
                      {
                        text: 'Engelle',
                        style: 'destructive',
                        onPress: () => {
                          void (async () => {
                            try {
                              await blockUser(user.uid, otherUserId);
                              await refreshProfile();
                              Alert.alert('Engellendi', 'Kullanıcı engellendi.', [
                                { text: 'Tamam', onPress: () => navigation.goBack() },
                              ]);
                            } catch (e) {
                              Alert.alert('Hata', e instanceof Error ? e.message : 'Engellenemedi.');
                            }
                          })();
                        },
                      },
                    ],
                  );
                },
              },
            ]}
          />
        </View>
      ),
    });
  }, [navigation, otherUserId, refreshProfile, user?.uid, user?.isAnonymous]);

  useEffect(() => {
    const event = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const sub = Keyboard.addListener(event, () => {
      requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
    });
    return () => sub.remove();
  }, []);

  async function sendLine(line: string) {
    if (user?.isAnonymous === true) {
      showGuestAlert(logout);
      return;
    }
    if (!chatId || !user?.uid || !line.trim()) {
      return;
    }
    try {
      await sendMessage(chatId, user.uid, line.trim());
      setText('');
    } catch (e) {
      Alert.alert(
        'Mesaj gönderilemedi',
        e instanceof Error ? e.message : 'Bağlantıyı kontrol edip tekrar dene.',
      );
    }
  }

  function confirmDeleteMessageForEveryone(messageId: string) {
    Alert.alert('Herkesten sil', 'Bu mesaj karşı taraftan da kaldırılacak. Emin misin?', [
      { text: 'Vazgeç', style: 'cancel' },
      {
        text: 'Sil',
        style: 'destructive',
        onPress: () => {
          if (!user?.uid) {
            return;
          }
          void (async () => {
            try {
              await deleteMessageForEveryone(chatId, messageId, user.uid);
            } catch (e) {
              Alert.alert('Silinemedi', e instanceof Error ? e.message : 'Tekrar dene.');
            }
          })();
        },
      },
    ]);
  }

  function openMessageActions(item: MessageDoc) {
    if (!user?.uid || user.isAnonymous) {
      return;
    }
    void impactLight();
    const mine = item.senderId === user.uid;
    const buttons: {
      text: string;
      style?: 'cancel' | 'destructive' | 'default';
      onPress?: () => void;
    }[] = [
      { text: 'İptal', style: 'cancel' },
      {
        text: 'Kendimden Sil',
        onPress: () => {
          void (async () => {
            try {
              await deleteMessageForMe(chatId, item.id, user.uid);
            } catch (e) {
              Alert.alert('Silinemedi', e instanceof Error ? e.message : 'Tekrar dene.');
            }
          })();
        },
      },
    ];
    if (mine) {
      buttons.push({
        text: 'Herkesten Sil',
        style: 'destructive',
        onPress: () => confirmDeleteMessageForEveryone(item.id),
      });
    }
    Alert.alert('Mesaj', undefined, buttons);
  }

  const empty = messages.length === 0;

  return (
    <KeyboardAvoidingView
      className="flex-1 bg-white dark:bg-black"
      style={{ flex: 1 }}
      behavior="padding"
      keyboardVerticalOffset={headerHeight}>
      <FlatList
        ref={listRef}
        className="flex-1 px-3"
        data={messages}
        keyExtractor={m => m.id}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={
          empty
            ? { flexGrow: 1, paddingTop: 12, paddingBottom: 24 + insets.bottom + 80 }
            : { paddingTop: 12, paddingBottom: 24 + insets.bottom + 80 }
        }
        onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: true })}
        ListEmptyComponent={
          empty ? (
            <View className="flex-1 justify-center px-5 py-10">
              {hideIce ? (
                <Text className="text-center text-sm text-zinc-500 dark:text-zinc-400">
                  Henüz mesaj yok.
                </Text>
              ) : (
                <>
                  <Text className="mb-5 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    Sohbeti başlatmak için bir buz kırıcı seçebilirsin:
                  </Text>
                  <View className="gap-3">
                    {EMPTY_CHAT_ICEBREAKERS.map(line => (
                      <TouchableOpacity
                        key={line}
                        activeOpacity={0.85}
                        onPress={() => {
                          if (user?.isAnonymous === true) {
                            showGuestAlert(logout);
                            return;
                          }
                          void sendLine(line);
                        }}
                        className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <Text className="text-center text-sm leading-5 text-zinc-800 dark:text-zinc-100">
                          {line}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </>
              )}
            </View>
          ) : null
        }
        renderItem={({ item }) => {
          const mine = item.senderId === user?.uid;
          return (
            <View className={`mb-2 flex-row px-1 ${mine ? 'justify-end' : 'justify-start'}`}>
              <Pressable
                onLongPress={() => openMessageActions(item)}
                delayLongPress={400}
                className={
                  mine
                    ? 'max-w-[80%] self-end rounded-2xl rounded-tr-none bg-zinc-900 px-3.5 py-2.5 active:opacity-90 dark:bg-zinc-50'
                    : 'max-w-[80%] rounded-2xl rounded-tl-none border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 active:opacity-90 dark:border-zinc-800 dark:bg-zinc-900'
                }>
                <Text
                  className={`text-sm leading-5 ${
                    mine ? 'text-white dark:text-zinc-950' : 'text-zinc-800 dark:text-zinc-100'
                  }`}>
                  {item.text}
                </Text>
              </Pressable>
            </View>
          );
        }}
      />
      <View
        className="flex-row items-end border-t border-zinc-200 bg-white px-3 pt-2 dark:border-zinc-800 dark:bg-black"
        style={{ paddingBottom: Math.max(insets.bottom, 10) }}>
        <TextInput
          className="mr-2 max-h-28 min-h-[44px] flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
          value={text}
          onChangeText={setText}
          placeholder="Mesaj yaz…"
          placeholderTextColor="#9ca3af"
          multiline
          scrollEnabled
          textAlignVertical="top"
          editable={!user?.isAnonymous}
        />
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => {
            if (user?.isAnonymous === true) {
              showGuestAlert(logout);
              return;
            }
            void impactLight();
            void sendLine(text);
          }}
          disabled={!text.trim() || user?.isAnonymous === true}
          className={`min-h-[44px] justify-center rounded-xl border px-4 py-2.5 ${
            text.trim() && user?.isAnonymous !== true
              ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-50 dark:bg-zinc-50'
              : 'border-zinc-200 bg-zinc-200 dark:border-zinc-800 dark:bg-zinc-800'
          }`}>
          <Text
            className={`text-sm font-bold ${
              text.trim() && user?.isAnonymous !== true
                ? 'text-white dark:text-zinc-950'
                : 'text-zinc-400 dark:text-zinc-500'
            }`}>
            Gönder
          </Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}
